import React, { useEffect, useMemo, useState } from "react";
import { DialogControl, FieldControl } from "spark-core-dx/components";
import { SystemTypes } from "spark-core-dx/services";
import {
  asArray,
  dedupeTransforms,
  normalizeDataTypeLabel,
  TRANSFORM_KIND_CUSTOM,
  TRANSFORM_KIND_ENTITY_LOOKUP,
  TRANSFORM_KIND_PREDEFINED,
  TRANSFORM_KIND_SOURCE_LOOKUP,
} from "./utils";

const CUSTOM_KEY = "custom";
const LOOKUP_SOURCE_KEY = "lookup_source";
const LOOKUP_ENTITY_KEY = "lookup_entity";
const DEFAULT_CUSTOM_TRANSFORM_SCRIPT = `if (value == null)
    return null;
// TODO: Transform incoming "value".
// Return the final mapped value.
return value; // Replace with your transformed value.`;

const LOOKUP_MATCH_OPTIONS = [
  { value: "==", label: "Exact Match" },
  { value: "StartsWith", label: "Starts With" },
  { value: "Contains", label: "Contains" },
];

const LOOKUP_NO_MATCH_OPTIONS = [
  { value: "ReturnNull", label: "Return Null" },
  { value: "KeepOriginal", label: "Keep Original" },
  { value: "Throw", label: "Throw Error" },
];

const LOOKUP_MULTI_MATCH_OPTIONS = [
  { value: "First", label: "Use First Match" },
  { value: "Throw", label: "Throw Error" },
];

const WRAPPED_ARROW_IIFE_REGEX = /^\s*\(\s*\(\s*\)\s*=>\s*\{([\s\S]*)\}\s*\)\s*\(\s*\)\s*;?\s*$/;
const WRAPPED_FUNCTION_IIFE_REGEX = /^\s*\(\s*function\s*\(\s*\)\s*\{([\s\S]*)\}\s*\)\s*\(\s*\)\s*;?\s*$/;

const toEditableScriptBody = (script) => {
  const text = String(script || "");
  if (!text.trim()) return "";
  const arrowMatch = text.match(WRAPPED_ARROW_IIFE_REGEX);
  if (arrowMatch && arrowMatch[1] != null) return String(arrowMatch[1]).trim();
  const fnMatch = text.match(WRAPPED_FUNCTION_IIFE_REGEX);
  if (fnMatch && fnMatch[1] != null) return String(fnMatch[1]).trim();
  return text;
};

const toStoredWrappedScript = (body) => {
  const text = String(body || "");
  if (!text.trim()) return "";
  if (WRAPPED_ARROW_IIFE_REGEX.test(text) || WRAPPED_FUNCTION_IIFE_REGEX.test(text)) {
    return text.trim();
  }
  return `(() => {\n${text.trim()}\n})();`;
};

const findFieldMetadata = (metadata, fieldName) => {
  const fields = Array.isArray(metadata?.Fields) ? metadata.Fields : [];
  const target = String(fieldName || "").trim().toLowerCase();
  return fields.find((field) => String(field?.FieldName || "").trim().toLowerCase() === target) || null;
};

const getHeaderFieldLabel = (field, fallbackConstant = false) => {
  if (fallbackConstant) return <span className="efmd-transform-header-field-name">(constant)</span>;
  const displayName = field?.displayName || field?.fieldName || "";
  const dataType = normalizeDataTypeLabel(field?.dataTypeName || field?.dataTypeId || "");
  if (!dataType) return <span className="efmd-transform-header-field-name">{displayName}</span>;
  return (
    <>
      <span className="efmd-transform-header-field-name">{displayName}</span>{" "}
      <span className="efmd-transform-header-field-type">({dataType})</span>
    </>
  );
};

const buildSearchSelectMetadata = ({ fieldName, displayName, dataJson, placeHolder = "Please Select" }) => ({
  FieldName: fieldName,
  DisplayName: displayName,
  FieldType: SystemTypes.UIFieldType.SearchSelectList,
  DataJson: dataJson,
  PlaceHolder: placeHolder,
});

const buildSelectListMetadata = ({ fieldName, displayName, dataJson, placeHolder = "Please Select" }) => ({
  FieldName: fieldName,
  DisplayName: displayName,
  FieldType: SystemTypes.UIFieldType.SelectList,
  DataJson: dataJson,
  PlaceHolder: placeHolder,
});

const buildPositiveIntegerMetadata = ({ fieldName, displayName }) => ({
  FieldName: fieldName,
  DisplayName: displayName,
  FieldType: SystemTypes.UIFieldType.TextBox,
  ValidationType: SystemTypes.UIFieldValidationType.IntPos,
});

const toSelectData = (items, getValue, getText) =>
  asArray(items).map((item) => ({
    Value: getValue(item),
    Text: getText(item),
  }));

const dedupeById = (items) => {
  const seen = new Set();
  return asArray(items).filter((item) => {
    const key = String(item?.id || item?.Value || item?.value || "").trim().toLowerCase();
    if (!key || seen.has(key)) return false;
    seen.add(key);
    return true;
  });
};

const TransformToolkitDialog = ({
  open,
  mapping,
  transforms,
  sourceField,
  entityField,
  sourceObjects = [],
  entityObjects = [],
  sourceObjectFieldMapItemMetadata,
  sourceObjectFieldTransformScriptItemMetadata,
  hideImportance = false,
  onCancel,
  onSave,
  onDelete,
}) => {
  const [search, setSearch] = useState("");
  const [pipeline, setPipeline] = useState([]);
  const [selectedIndex, setSelectedIndex] = useState(-1);
  const [constantValue, setConstantValue] = useState(mapping?.constantValue || mapping?.specialValue || "");
  const [importance, setImportance] = useState(255);
  const [legacyCustomScript, setLegacyCustomScript] = useState("");
  const [isMultiTransformMode, setIsMultiTransformMode] = useState(true);
  const [collapsedGroups, setCollapsedGroups] = useState({});

  const clampImportance = (value) => {
    const parsed = Number(value);
    if (!Number.isFinite(parsed)) return 255;
    const rounded = Math.round(parsed);
    if (rounded < 1) return 1;
    if (rounded > 255) return 255;
    return rounded;
  };

  const isLookupKind = (kind) => kind === LOOKUP_SOURCE_KEY || kind === LOOKUP_ENTITY_KEY;

  const getLookupObjectType = (step, kind) => {
    if (kind === LOOKUP_ENTITY_KEY) return "EntityObject";
    if (kind === LOOKUP_SOURCE_KEY) return "SourceObject";
    return step?.lookupObjectType || step?.LookupObjectType || null;
  };

  const normalizeStep = (step, index) => {
    if (!step) return null;
    const key = step.key || step.transformKey || step.TransformKey || "";
    const rawCode = step.code || step.customTransformScript || step.CustomTransformScript || step.transformCode || step.TransformCode || "";
    const code = toEditableScriptBody(rawCode);
    const incomingKind = step.kind || null;
    const transformKind =
      step.transformKind ||
      step.TransformKind ||
      step.raw?.TransformKind ||
      step.raw?.transformKind ||
      (step.LookupEntityObjectId || step.lookupEntityObjectId
        ? TRANSFORM_KIND_ENTITY_LOOKUP
        : step.LookupSourceObjectId || step.lookupSourceObjectId
          ? TRANSFORM_KIND_SOURCE_LOOKUP
          : "") ||
      (String(step.lookupObjectType || step.LookupObjectType || "").toLowerCase() === "entityobject"
        ? TRANSFORM_KIND_ENTITY_LOOKUP
        : String(step.lookupObjectType || step.LookupObjectType || "").toLowerCase() === "sourceobject"
          ? TRANSFORM_KIND_SOURCE_LOOKUP
          : "");
    const kind =
      incomingKind ||
      (transformKind === TRANSFORM_KIND_ENTITY_LOOKUP
        ? LOOKUP_ENTITY_KEY
        : transformKind === TRANSFORM_KIND_SOURCE_LOOKUP
          ? LOOKUP_SOURCE_KEY
          : String(key).toLowerCase() === CUSTOM_KEY || transformKind === TRANSFORM_KIND_CUSTOM
            ? "custom"
            : "predefined");
    const displayName =
      step.displayName ||
      step.customName ||
      step.CustomName ||
      step.transformDisplayName ||
      step.TransformDisplayName ||
      (kind === "custom"
        ? "Custom"
        : kind === LOOKUP_ENTITY_KEY
          ? "Entity Lookup"
          : kind === LOOKUP_SOURCE_KEY
            ? "Source Lookup"
            : key);
    return {
      id: step.id || `ts_${Date.now()}_${index}`,
      kind,
      transformKind:
        transformKind ||
        (kind === LOOKUP_ENTITY_KEY
          ? TRANSFORM_KIND_ENTITY_LOOKUP
          : kind === LOOKUP_SOURCE_KEY
            ? TRANSFORM_KIND_SOURCE_LOOKUP
            : kind === "custom"
              ? TRANSFORM_KIND_CUSTOM
              : TRANSFORM_KIND_PREDEFINED),
      key,
      displayName,
      code: kind === "custom" && !String(code || "").trim() ? DEFAULT_CUSTOM_TRANSFORM_SCRIPT : code,
      isActive: step?.isActive !== false && step?.IsActive !== false,
      lookupObjectType: getLookupObjectType(step, kind),
      lookupObjectId:
        step?.lookupObjectId ||
        step?.LookupObjectId ||
        step?.lookupEntityObjectId ||
        step?.LookupEntityObjectId ||
        step?.lookupSourceObjectId ||
        step?.LookupSourceObjectId ||
        null,
      lookupSearchFieldId:
        step?.lookupSearchFieldId ||
        step?.LookupSearchFieldId ||
        step?.lookupEntitySearchFieldId ||
        step?.LookupEntitySearchFieldId ||
        step?.lookupSourceSearchFieldId ||
        step?.LookupSourceSearchFieldId ||
        null,
      lookupResultFieldId:
        step?.lookupResultFieldId ||
        step?.LookupResultFieldId ||
        step?.lookupEntityResultFieldId ||
        step?.LookupEntityResultFieldId ||
        step?.lookupSourceResultFieldId ||
        step?.LookupSourceResultFieldId ||
        null,
      lookupMatchMethod: step?.lookupMatchMethod || step?.LookupMatchMethod || "==",
      lookupIgnoreCase: step?.lookupIgnoreCase !== false && step?.LookupIgnoreCase !== false,
      lookupNoMatchBehavior: step?.lookupNoMatchBehavior || step?.LookupNoMatchBehavior || "ReturnNull",
      lookupMultipleMatchBehavior: step?.lookupMultipleMatchBehavior || step?.LookupMultipleMatchBehavior || "First",
      lookupPageSize: Number(step?.lookupPageSize || step?.LookupPageSize || 1000) || 1000,
      lookupMaxPages: Number(step?.lookupMaxPages || step?.LookupMaxPages || 100) || 100,
      raw: step?.raw || step || null,
    };
  };

  const buildInitialPipeline = (mapItem) => {
    const fromSteps = (mapItem?.transformSteps || [])
      .map((step, idx) => normalizeStep(step, idx))
      .filter(Boolean);

    if (fromSteps.length > 0) return fromSteps;

    const key = mapItem?.transformKey;
    const code = (mapItem?.transformCode || "").trim();
    if (key && key !== CUSTOM_KEY) {
      return [{ id: `ts_${Date.now()}_0`, kind: "predefined", key, displayName: mapItem?.transformDisplayName || key, code: "" }];
    }
    if (code || key === CUSTOM_KEY) {
      return [{
        id: `ts_${Date.now()}_0`,
        kind: "custom",
        key: CUSTOM_KEY,
        displayName: "Custom",
        code: String(mapItem?.transformCode || "").trim() ? toEditableScriptBody(mapItem?.transformCode) : DEFAULT_CUSTOM_TRANSFORM_SCRIPT,
      }];
    }
    return [];
  };

  useEffect(() => {
    const nextPipeline = buildInitialPipeline(mapping);
    const rawLegacyScript = String(mapping?.raw?.CustomTransformScript || mapping?.raw?.customTransformScript || "").trim();
    setPipeline(nextPipeline);
    setSelectedIndex(nextPipeline.length > 0 ? 0 : -1);
    setSearch("");
    setConstantValue(mapping?.constantValue || mapping?.specialValue || "");
    setImportance(clampImportance(mapping?.importance ?? mapping?.raw?.Importance ?? 255));
    const initialLegacy = mapping?.raw?.CustomTransformScript || mapping?.raw?.customTransformScript || "";
    const editableLegacy = toEditableScriptBody(initialLegacy);
    setLegacyCustomScript(String(editableLegacy || "").trim() ? editableLegacy : DEFAULT_CUSTOM_TRANSFORM_SCRIPT);
    setIsMultiTransformMode(rawLegacyScript.length === 0);
  }, [mapping]);

  const selectedStep = selectedIndex >= 0 ? pipeline[selectedIndex] : null;
  const sourceLookupObjects = useMemo(
    () => dedupeById(asArray(sourceObjects).filter((item) => item?.id)),
    [sourceObjects]
  );
  const entityLookupObjects = useMemo(
    () => dedupeById(asArray(entityObjects).filter((item) => item?.id)),
    [entityObjects]
  );
  const lookupObjectOptions = useMemo(() => {
    if (!selectedStep) return [];
    return selectedStep.kind === LOOKUP_ENTITY_KEY ? entityLookupObjects : sourceLookupObjects;
  }, [selectedStep, entityLookupObjects, sourceLookupObjects]);
  const selectedLookupObject = useMemo(
    () => lookupObjectOptions.find((item) => item.id === selectedStep?.lookupObjectId) || null,
    [lookupObjectOptions, selectedStep?.lookupObjectId]
  );
  const lookupFieldOptions = useMemo(
    () => dedupeById(asArray(selectedLookupObject?.fields).filter((field) => field?.id)),
    [selectedLookupObject]
  );
  const lookupObjectSelectData = useMemo(
    () => toSelectData(
      lookupObjectOptions,
      (item) => item.id,
      (item) => item.displayName || item.objectName || item.id
    ),
    [lookupObjectOptions]
  );
  const lookupFieldSelectData = useMemo(
    () => toSelectData(
      lookupFieldOptions,
      (field) => field.id,
      (field) => field.displayName || field.fieldName || field.id
    ),
    [lookupFieldOptions]
  );
  const lookupObjectFieldMetadata = useMemo(
    () =>
      buildSearchSelectMetadata({
        fieldName: "LookupObjectId",
        displayName: "Lookup Object",
        dataJson: lookupObjectSelectData,
        placeHolder: "Search objects...",
      }),
    [lookupObjectSelectData]
  );
  const lookupSearchFieldMetadata = useMemo(
    () =>
      buildSearchSelectMetadata({
        fieldName: "LookupSearchFieldId",
        displayName: "Search Field",
        dataJson: lookupFieldSelectData,
        placeHolder: "Search fields...",
      }),
    [lookupFieldSelectData]
  );
  const lookupResultFieldMetadata = useMemo(
    () =>
      buildSearchSelectMetadata({
        fieldName: "LookupResultFieldId",
        displayName: "Result Field",
        dataJson: lookupFieldSelectData,
        placeHolder: "Search fields...",
      }),
    [lookupFieldSelectData]
  );
  const lookupMatchFieldMetadata = useMemo(
    () =>
      buildSelectListMetadata({
        fieldName: "LookupMatchMethod",
        displayName: "Match Method",
        dataJson: toSelectData(LOOKUP_MATCH_OPTIONS, (option) => option.value, (option) => option.label),
      }),
    []
  );
  const lookupNoMatchFieldMetadata = useMemo(
    () =>
      buildSelectListMetadata({
        fieldName: "LookupNoMatchBehavior",
        displayName: "No Match",
        dataJson: toSelectData(LOOKUP_NO_MATCH_OPTIONS, (option) => option.value, (option) => option.label),
      }),
    []
  );
  const lookupMultipleMatchFieldMetadata = useMemo(
    () =>
      buildSelectListMetadata({
        fieldName: "LookupMultipleMatchBehavior",
        displayName: "Multiple Matches",
        dataJson: toSelectData(LOOKUP_MULTI_MATCH_OPTIONS, (option) => option.value, (option) => option.label),
      }),
    []
  );
  const lookupIgnoreCaseFieldMetadata = useMemo(
    () =>
      buildSelectListMetadata({
        fieldName: "LookupIgnoreCase",
        displayName: "Ignore Case",
        dataJson: [
          { Value: "true", Text: "Yes" },
          { Value: "false", Text: "No" },
        ],
      }),
    []
  );
  const lookupPageSizeFieldMetadata = useMemo(
    () => buildPositiveIntegerMetadata({ fieldName: "LookupPageSize", displayName: "Page Size" }),
    []
  );
  const lookupMaxPagesFieldMetadata = useMemo(
    () => buildPositiveIntegerMetadata({ fieldName: "LookupMaxPages", displayName: "Max Pages" }),
    []
  );
  const operationItemContext = useMemo(() => {
    const selectedCode =
      selectedStep?.kind === "custom"
        ? toStoredWrappedScript(selectedStep?.code || "")
        : (selectedStep?.code || "");
    return {
      ...(mapping?.raw || {}),
      SourceField: sourceField?.raw || sourceField || null,
      EntityField: entityField?.raw || entityField || null,
      CustomTransformScript: isMultiTransformMode ? selectedCode : toStoredWrappedScript(String(legacyCustomScript || "")),
      CustomName: selectedStep?.displayName || "",
    };
  }, [mapping?.raw, sourceField, entityField, isMultiTransformMode, legacyCustomScript, selectedStep?.code, selectedStep?.displayName, selectedStep?.kind]);
  const customJavaScriptFieldMetadata = useMemo(
    () =>
      findFieldMetadata(sourceObjectFieldTransformScriptItemMetadata, "CustomTransformScript") ||
      findFieldMetadata(sourceObjectFieldMapItemMetadata, "CustomTransformScript") || {
        FieldName: "CustomTransformScript",
        DisplayName: "Custom JavaScript",
        FieldType: SystemTypes.UIFieldType.JS,
      },
    [sourceObjectFieldMapItemMetadata, sourceObjectFieldTransformScriptItemMetadata]
  );
  const legacyMapCustomJavaScriptFieldMetadata = useMemo(
    () =>
      findFieldMetadata(sourceObjectFieldMapItemMetadata, "CustomTransformScript") || {
        FieldName: "CustomTransformScript",
        DisplayName: "Custom Transform",
        FieldType: SystemTypes.UIFieldType.JS,
      },
    [sourceObjectFieldMapItemMetadata]
  );
  const filteredTransforms = useMemo(() => {
    const q = String(search || "").trim().toLowerCase();
    const catalog = dedupeTransforms(transforms || []);
    if (!q) return catalog;
    return catalog.filter((t) => {
      const label = String(t.displayName || t.name || t.key || t.id || "").toLowerCase();
      return label.includes(q);
    });
  }, [search, transforms]);
  const groupedTransforms = useMemo(() => {
    const groups = [];
    const byGroup = new Map();

    filteredTransforms.forEach((transform) => {
      const groupName = String(transform?.group || transform?.Group || "Other").trim() || "Other";
      if (!byGroup.has(groupName)) {
        const entry = { group: groupName, items: [] };
        byGroup.set(groupName, entry);
        groups.push(entry);
      }
      byGroup.get(groupName).items.push(transform);
    });

    return groups;
  }, [filteredTransforms]);

  useEffect(() => {
    const nextGroups = new Set(
      dedupeTransforms(Array.isArray(transforms) ? transforms : []).map((t) => String(t?.group || t?.Group || "Other").trim() || "Other")
    );
    setCollapsedGroups((previous) => {
      const next = { ...previous };
      nextGroups.forEach((groupName) => {
        if (next[groupName] == null) next[groupName] = true;
      });
      Object.keys(next).forEach((groupName) => {
        if (!nextGroups.has(groupName)) delete next[groupName];
      });
      return next;
    });
  }, [transforms]);

  const toggleGroupCollapsed = (groupName) => {
    setCollapsedGroups((previous) => {
      const currentlyCollapsed = previous[groupName] === true;
      const next = {};
      Object.keys(previous).forEach((name) => {
        next[name] = true;
      });
      next[groupName] = !currentlyCollapsed ? true : false;
      return next;
    });
  };

  const typeMismatch = useMemo(() => {
    if (mapping?.isConstant) return false;
    const sourceType = String(sourceField?.dataTypeId || sourceField?.dataTypeName || "").toLowerCase();
    const targetType = String(entityField?.dataTypeId || entityField?.dataTypeName || "").toLowerCase();
    if (!sourceType || !targetType) return false;
    return sourceType !== targetType;
  }, [mapping?.isConstant, sourceField, entityField]);

  const pipelineHasEmptyCustom = useMemo(() => {
    return pipeline.some((step) => step.kind === "custom" && !String(step.code || "").trim());
  }, [pipeline]);
  const pipelineHasEmptyCustomName = useMemo(() => {
    return pipeline.some((step) => step.kind === "custom" && !String(step.displayName || "").trim());
  }, [pipeline]);
  const pipelineHasInvalidLookup = useMemo(() => {
    return pipeline.some((step) =>
      isLookupKind(step?.kind) &&
      (!step?.lookupObjectId || !step?.lookupSearchFieldId || !step?.lookupResultFieldId)
    );
  }, [pipeline]);
  const showTypeMismatchWarning = typeMismatch && pipeline.length === 0;
  const showLegacyTypeMismatchWarning = typeMismatch && !String(legacyCustomScript || "").trim();
  const validationWarnings = useMemo(() => {
    const warnings = [];
    if (isMultiTransformMode) {
      if (showTypeMismatchWarning) warnings.push("Type mismatch: add at least one transform step.");
      if (pipelineHasEmptyCustom) warnings.push("Custom step has empty code.");
      if (pipelineHasEmptyCustomName) warnings.push("Custom Name is required for custom step(s).");
      if (pipelineHasInvalidLookup) warnings.push("Lookup step requires an object, a search field, and a result field.");
    } else if (showLegacyTypeMismatchWarning) {
      warnings.push("Type mismatch: enter a custom transform script.");
    }
    return warnings;
  }, [isMultiTransformMode, showTypeMismatchWarning, pipelineHasEmptyCustom, pipelineHasEmptyCustomName, pipelineHasInvalidLookup, showLegacyTypeMismatchWarning]);

  const canApply = useMemo(() => {
    if (isMultiTransformMode) {
      if (pipelineHasEmptyCustom) return false;
      if (pipelineHasEmptyCustomName) return false;
      if (pipelineHasInvalidLookup) return false;
      if (showTypeMismatchWarning) return false;
      return true;
    }
    if (showLegacyTypeMismatchWarning) return false;
    return true;
  }, [isMultiTransformMode, pipelineHasEmptyCustom, pipelineHasEmptyCustomName, pipelineHasInvalidLookup, showTypeMismatchWarning, showLegacyTypeMismatchWarning]);

  const addCustom = () => {
    const next = {
      id: `ts_${Date.now()}_${Math.random().toString(16).slice(2)}`,
      kind: "custom",
      transformKind: TRANSFORM_KIND_CUSTOM,
      key: CUSTOM_KEY,
      displayName: "",
      code: DEFAULT_CUSTOM_TRANSFORM_SCRIPT,
      isActive: true,
    };
    setPipeline((prev) => [...prev, next]);
    setSelectedIndex(pipeline.length);
  };

  const addPredefined = (transform) => {
    const key = transform?.key || transform?.id;
    if (!key) return;
    const next = {
      id: `ts_${Date.now()}_${Math.random().toString(16).slice(2)}`,
      kind: "predefined",
      transformKind: TRANSFORM_KIND_PREDEFINED,
      key,
      displayName: transform?.displayName || transform?.name || key,
      code: transform?.code || "",
      isActive: true,
    };
    setPipeline((prev) => [...prev, next]);
    setSelectedIndex(pipeline.length);
  };

  const addLookup = (kind) => {
    const useEntityObjects = kind === LOOKUP_ENTITY_KEY;
    const next = {
      id: `ts_${Date.now()}_${Math.random().toString(16).slice(2)}`,
      kind,
      transformKind: useEntityObjects ? TRANSFORM_KIND_ENTITY_LOOKUP : TRANSFORM_KIND_SOURCE_LOOKUP,
      key: null,
      displayName: useEntityObjects ? "Entity Lookup" : "Source Lookup",
      code: "",
      isActive: true,
      lookupObjectType: useEntityObjects ? "EntityObject" : "SourceObject",
      lookupObjectId: null,
      lookupSearchFieldId: null,
      lookupResultFieldId: null,
      lookupMatchMethod: "==",
      lookupIgnoreCase: true,
      lookupNoMatchBehavior: "ReturnNull",
      lookupMultipleMatchBehavior: "First",
      lookupPageSize: 1000,
      lookupMaxPages: 100,
    };
    setPipeline((prev) => [...prev, next]);
    setSelectedIndex(pipeline.length);
  };

  const updateSelectedStep = (patch) => {
    if (selectedIndex < 0) return;
    setPipeline((prev) => prev.map((step, idx) => (idx === selectedIndex ? { ...step, ...patch } : step)));
  };

  const removeAt = (index) => {
    if (index < 0) return;
    setPipeline((prev) => {
      const next = prev.slice();
      next.splice(index, 1);
      if (next.length === 0) {
        setSelectedIndex(-1);
      } else if (selectedIndex >= next.length) {
        setSelectedIndex(next.length - 1);
      } else if (selectedIndex === index) {
        setSelectedIndex(Math.max(0, index - 1));
      }
      return next;
    });
  };

  const moveAt = (index, direction) => {
    const target = index + direction;
    if (index < 0 || target < 0 || target >= pipeline.length) return;
    setPipeline((prev) => {
      const next = prev.slice();
      const tmp = next[index];
      next[index] = next[target];
      next[target] = tmp;
      return next;
    });
    if (selectedIndex === index) setSelectedIndex(target);
    else if (selectedIndex === target) setSelectedIndex(index);
  };

  const handleSave = async () => {
    if (!canApply) return { Success: false };

    const effectivePipeline = isMultiTransformMode ? pipeline : [];
    const first = effectivePipeline[0] || null;
    const transformKey = first ? first.key : null;
    const transformDisplayName = first ? first.displayName : "";
    const effectiveStoredPipeline = effectivePipeline.map((step) => ({
      ...step,
      code: step?.kind === "custom" ? toStoredWrappedScript(step?.code || "") : (step?.code || ""),
    }));
    const transformCode = isMultiTransformMode
      ? (effectiveStoredPipeline[0] ? (effectiveStoredPipeline[0].code || "") : "")
      : toStoredWrappedScript(String(legacyCustomScript || ""));

    onSave({
      transformKey,
      transformDisplayName,
      transformCode,
      transformSteps: effectiveStoredPipeline.map((step) => ({ ...step })),
      multiTransformMode: isMultiTransformMode,
      legacyCustomTransformScript: isMultiTransformMode ? "" : toStoredWrappedScript(String(legacyCustomScript || "")),
      importance: clampImportance(importance),
      constantValue: mapping?.isConstant ? constantValue : undefined,
    });
    return { Success: true };
  };

  return (
    <DialogControl
      openDialog={open}
      title={
        <div className="efmd-transform-dialog-title">
          <span>Transform Toolkit</span>
          <label className="efmd-transform-mode-toggle" title="Toggle between multi-step transform scripts and legacy single custom transform script.">
            <input
              type="checkbox"
              checked={isMultiTransformMode}
              onChange={(event) => {
                const checked = event.target.checked;
                setIsMultiTransformMode(checked);
                if (checked && pipeline.length === 0 && String(legacyCustomScript || "").trim()) {
                  setPipeline([
                    {
                      id: `ts_${Date.now()}_legacy`,
                      kind: "custom",
                      key: CUSTOM_KEY,
                      displayName: "Custom",
                      code: legacyCustomScript,
                    },
                  ]);
                  setSelectedIndex(0);
                }
              }}
            />
            <span>Use Pipeline</span>
          </label>
        </div>
      }
      className={`efmd-transform-dialog${canApply ? "" : " is-apply-disabled"}`}
      onX={onCancel}
      onCancel={onCancel}
      onOk={handleSave}
      okText={"Apply"}
      customCenterText={"Remove Mapping"}
      customCenterType={"cancel"}
      onCustomCenter={() => {
        onDelete();
        return { Success: true };
      }}
    >
      <div className="efmd-dialog">
        <div className="efmd-transform-header">
          <div className="efmd-transform-header-field">{getHeaderFieldLabel(sourceField, mapping?.isConstant)}</div>
          <div className="efmd-transform-arrow" title="Maps source to entity">→</div>
          <div className="efmd-transform-header-field">{getHeaderFieldLabel(entityField, false)}</div>
          {!hideImportance && (
            <div className="efmd-transform-importance-row">
              <div
                className="efmd-transform-title"
                title="Importance controls how mappings across different source objects are prioritized together. Lower numbers are more important and have more weight when determining the best representation of data."
              >
                Importance (1-255)
              </div>
              <input
                type="number"
                min={1}
                max={255}
                className="efmd-transform-input efmd-transform-input--importance"
                title="Importance controls how mappings across different source objects are prioritized together. Lower numbers are more important and have more weight when determining the best representation of data."
                value={importance}
                onChange={(event) => {
                  const nextValue = event.target.value;
                  if (nextValue === "") {
                    setImportance(255);
                    return;
                  }
                  setImportance(clampImportance(nextValue));
                }}
              />
            </div>
          )}
        </div>

        {mapping?.isConstant && (
          <div>
            <div className="efmd-transform-title">Constant Value</div>
            <input className="efmd-transform-input" value={constantValue} onChange={(event) => setConstantValue(event.target.value)} />
          </div>
        )}

        {validationWarnings.length > 0 && <div className="efmd-transform-warn">{validationWarnings.join(" ")}</div>}

        {isMultiTransformMode ? (
          <div className="efmd-transform-grid">
            <div className="efmd-transform-pane efmd-transform-pane--add">
              <div className="efmd-transform-title">Add Step</div>
              <div className="efmd-transform-title-row">
                <div className="efmd-transform-actions">
                  <button type="button" className="efmd-transform-btn" onClick={addCustom}>+ Custom</button>
                  <button
                    type="button"
                    className="efmd-transform-btn"
                    onClick={() => addLookup(LOOKUP_SOURCE_KEY)}
                    disabled={sourceLookupObjects.length === 0}
                    title={sourceLookupObjects.length === 0 ? "No source objects are available for lookup." : "Add a source-object lookup step."}
                  >
                    + Source Lookup
                  </button>
                  <button
                    type="button"
                    className="efmd-transform-btn"
                    onClick={() => addLookup(LOOKUP_ENTITY_KEY)}
                    disabled={entityLookupObjects.length === 0}
                    title={entityLookupObjects.length === 0 ? "No entity objects are available for lookup." : "Add an entity-object lookup step."}
                  >
                    + Entity Lookup
                  </button>
                </div>
              </div>
              <input
                className="efmd-transform-input"
                value={search}
                placeholder="Search transforms..."
                onChange={(event) => setSearch(event.target.value)}
              />
              <div className="efmd-transform-list efmd-transform-list--tree">
                {groupedTransforms.length === 0 && (
                  <div className="efmd-transform-tree-empty">No transforms found.</div>
                )}
                {groupedTransforms.map((groupEntry) => {
                  const groupName = groupEntry.group;
                  const isCollapsed = collapsedGroups[groupName] === true;
                  return (
                    <div className="efmd-transform-tree-group" key={groupName}>
                      <button
                        type="button"
                        className="efmd-transform-tree-group-header"
                        onClick={() => toggleGroupCollapsed(groupName)}
                      >
                        <span className={`efmd-transform-tree-group-caret${isCollapsed ? " is-collapsed" : ""}`}>▼</span>
                        <span className="efmd-transform-tree-group-name">{groupName}</span>
                        <span className="efmd-transform-tree-group-count">({groupEntry.items.length})</span>
                      </button>
                      {!isCollapsed && (
                        <div className="efmd-transform-tree-children">
                          {groupEntry.items.map((transform) => (
                            <button
                              key={transform.key || transform.id}
                              type="button"
                              className="efmd-transform-item efmd-transform-tree-item"
                              title={String(transform?.description || transform?.Description || transform?.displayName || transform?.name || transform?.key || "")}
                              onClick={() => addPredefined(transform)}
                            >
                              {transform.displayName || transform.name || transform.key || transform.id}
                            </button>
                          ))}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>

            <div className="efmd-transform-pane efmd-transform-pane--pipeline">
              <div className="efmd-transform-title">Pipeline ({pipeline.length === 0 ? 1 : pipeline.length})</div>
              <div className="efmd-transform-list">
                {pipeline.length === 0 && (
                  <div className="efmd-transform-item is-row efmd-transform-item--placeholder">
                    <span title="Passthrough means no transform steps are applied; the input value is used as-is.">
                      Passthrough
                    </span>
                  </div>
                )}
                {pipeline.map((step, idx) => (
                  <div
                    key={step.id}
                    className={`efmd-transform-item is-row ${selectedIndex === idx ? "is-selected" : ""} ${step?.isActive === false ? "is-inactive" : ""}`}
                    onClick={() => setSelectedIndex(idx)}
                  >
                    <span className="efmd-transform-step-main">
                      <span className="efmd-transform-step-index">{idx + 1}.</span>
                      <input
                        type="text"
                        className="efmd-transform-pipeline-name-input"
                        value={step.displayName || ""}
                        placeholder={step.kind === "custom" ? "(Required)" : String(step.key || "")}
                        onClick={(event) => {
                          event.stopPropagation();
                          setSelectedIndex(idx);
                        }}
                        onChange={(event) => {
                          const nextValue = event.target.value;
                          setPipeline((prev) => prev.map((entry, entryIdx) => (entryIdx === idx ? { ...entry, displayName: nextValue } : entry)));
                        }}
                      />
                    </span>
                    <span className="efmd-transform-inline-actions">
                      <label className="efmd-transform-active-toggle" title="Enable or disable this transform step.">
                        <input
                          type="checkbox"
                          checked={step?.isActive !== false}
                          onChange={(event) => {
                            event.stopPropagation();
                            const checked = event.target.checked;
                            setPipeline((prev) => prev.map((entry, entryIdx) => (entryIdx === idx ? { ...entry, isActive: checked } : entry)));
                          }}
                        />
                      </label>
                      <button type="button" className="efmd-transform-btn efmd-transform-btn--icon" title="Move up" onClick={(event) => { event.stopPropagation(); moveAt(idx, -1); }}>↑</button>
                      <button type="button" className="efmd-transform-btn efmd-transform-btn--icon" title="Move down" onClick={(event) => { event.stopPropagation(); moveAt(idx, 1); }}>↓</button>
                      <button type="button" className="efmd-transform-btn efmd-transform-btn--icon efmd-transform-btn--danger" title="Remove step" onClick={(event) => { event.stopPropagation(); removeAt(idx); }}>✕</button>
                    </span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        ) : (
          <FieldControl
            className="efmd-transform-code-control"
            fieldMetadata={legacyMapCustomJavaScriptFieldMetadata}
            updatedItem={operationItemContext}
            value={legacyCustomScript}
            onChange={(value) => setLegacyCustomScript(value)}
          />
        )}

        {isMultiTransformMode && selectedStep?.kind === "custom" && (
          <div>
            <FieldControl
              className="efmd-transform-code-control"
              fieldMetadata={customJavaScriptFieldMetadata}
              updatedItem={operationItemContext}
              value={selectedStep.code || ""}
              onChange={(value) => {
                setPipeline((prev) => prev.map((step, idx) => (idx === selectedIndex ? { ...step, code: value } : step)));
              }}
            />
          </div>
        )}

        {isMultiTransformMode && isLookupKind(selectedStep?.kind) && (
          <div className="efmd-transform-lookup-editor">
            <div className="efmd-transform-lookup-grid">
              <div className="efmd-transform-lookup-field">
                <FieldControl
                  className="efmd-transform-lookup-control"
                  fieldMetadata={lookupObjectFieldMetadata}
                  updatedItem={operationItemContext}
                  value={selectedStep?.lookupObjectId || ""}
                  disableValidation={true}
                  onChange={(value) => {
                    const nextObjectId = value || null;
                    updateSelectedStep({
                      lookupObjectType: selectedStep?.kind === LOOKUP_ENTITY_KEY ? "EntityObject" : "SourceObject",
                      lookupObjectId: nextObjectId,
                      lookupSearchFieldId: null,
                      lookupResultFieldId: null,
                    });
                  }}
                />
              </div>

              <div className="efmd-transform-lookup-field">
                <FieldControl
                  className="efmd-transform-lookup-control"
                  fieldMetadata={lookupSearchFieldMetadata}
                  updatedItem={operationItemContext}
                  value={selectedStep?.lookupSearchFieldId || ""}
                  disabled={lookupFieldOptions.length === 0}
                  disableValidation={true}
                  onChange={(value) => updateSelectedStep({ lookupSearchFieldId: value || null })}
                />
              </div>

              <div className="efmd-transform-lookup-field">
                <FieldControl
                  className="efmd-transform-lookup-control"
                  fieldMetadata={lookupResultFieldMetadata}
                  updatedItem={operationItemContext}
                  value={selectedStep?.lookupResultFieldId || ""}
                  disabled={lookupFieldOptions.length === 0}
                  disableValidation={true}
                  onChange={(value) => updateSelectedStep({ lookupResultFieldId: value || null })}
                />
              </div>

              <div className="efmd-transform-lookup-field">
                <FieldControl
                  className="efmd-transform-lookup-control"
                  fieldMetadata={lookupMatchFieldMetadata}
                  updatedItem={operationItemContext}
                  value={selectedStep?.lookupMatchMethod || "=="}
                  disableValidation={true}
                  onChange={(value) => updateSelectedStep({ lookupMatchMethod: value || "==" })}
                />
              </div>

              <div className="efmd-transform-lookup-field">
                <FieldControl
                  className="efmd-transform-lookup-control"
                  fieldMetadata={lookupNoMatchFieldMetadata}
                  updatedItem={operationItemContext}
                  value={selectedStep?.lookupNoMatchBehavior || "ReturnNull"}
                  disableValidation={true}
                  onChange={(value) => updateSelectedStep({ lookupNoMatchBehavior: value || "ReturnNull" })}
                />
              </div>

              <div className="efmd-transform-lookup-field">
                <FieldControl
                  className="efmd-transform-lookup-control"
                  fieldMetadata={lookupMultipleMatchFieldMetadata}
                  updatedItem={operationItemContext}
                  value={selectedStep?.lookupMultipleMatchBehavior || "First"}
                  disableValidation={true}
                  onChange={(value) => updateSelectedStep({ lookupMultipleMatchBehavior: value || "First" })}
                />
              </div>

              <div className="efmd-transform-lookup-field">
                <FieldControl
                  className="efmd-transform-lookup-control"
                  fieldMetadata={lookupPageSizeFieldMetadata}
                  updatedItem={operationItemContext}
                  value={String(selectedStep?.lookupPageSize || 1000)}
                  disableValidation={true}
                  onChange={(value) =>
                    updateSelectedStep({ lookupPageSize: Math.max(1, Number(value || 1000) || 1000) })
                  }
                />
              </div>

              <div className="efmd-transform-lookup-field">
                <FieldControl
                  className="efmd-transform-lookup-control"
                  fieldMetadata={lookupIgnoreCaseFieldMetadata}
                  updatedItem={operationItemContext}
                  value={selectedStep?.lookupIgnoreCase !== false ? "true" : "false"}
                  disableValidation={true}
                  onChange={(value) => updateSelectedStep({ lookupIgnoreCase: String(value) !== "false" })}
                />
              </div>

              <div className="efmd-transform-lookup-field">
                <FieldControl
                  className="efmd-transform-lookup-control"
                  fieldMetadata={lookupMaxPagesFieldMetadata}
                  updatedItem={operationItemContext}
                  value={String(selectedStep?.lookupMaxPages || 100)}
                  disableValidation={true}
                  onChange={(value) =>
                    updateSelectedStep({ lookupMaxPages: Math.max(1, Number(value || 100) || 100) })
                  }
                />
              </div>
            </div>
          </div>
        )}
      </div>
    </DialogControl>
  );
};

export default TransformToolkitDialog;
